import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Plus, Check, Heart, Star, Lock, BadgeCheck, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { countryFlag, countryName, typeLabel, formatDuration, onImgError } from "@/lib/constants";

export default function MovieDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [tab, setTab] = useState("reviews");
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = () => api.get(`/content/${id}`).then((r) => setItem(r.data)).catch(() => setItem(false));
  useEffect(() => { load(); window.scrollTo(0, 0); /* eslint-disable-next-line */ }, [id]);

  if (item === false) return <Centered>Content not found.</Centered>;
  if (!item) return <Centered><div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" /></Centered>;

  const guard = () => { if (!user) { toast.error("Please sign in first"); navigate("/login"); return false; } return true; };

  const toggleLike = async () => {
    if (!guard()) return;
    const { data } = await api.post(`/content/${id}/like`);
    setItem({ ...item, liked: data.liked, like_count: data.like_count });
  };
  const toggleSave = async () => {
    if (!guard()) return;
    const { data } = await api.post(`/content/${id}/save`);
    setItem({ ...item, saved: data.saved });
    toast.success(data.saved ? "Added to My List" : "Removed from My List");
  };
  const submitReview = async () => {
    if (!guard()) return;
    try { await api.post(`/content/${id}/reviews`, { rating, text: reviewText }); setReviewText(""); toast.success("Review posted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitComment = async () => {
    if (!guard()) return;
    if (!comment.trim()) return;
    try { await api.post(`/content/${id}/comments`, { text: comment }); setComment(""); toast.success("Comment added"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const buyPPV = async () => {
    if (!guard()) return;
    try { await api.post("/purchases/ppv", { content_id: id }); toast.success("Unlocked! Enjoy the film."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const moderate = async (status) => {
    try { await api.put(`/admin/content/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="relative h-[55vh] min-h-[380px]">
        <img src={item.thumbnail} alt={item.title} onError={onImgError} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08070B] via-[#08070B]/60 to-[#08070B]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08070B]/80 to-transparent" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 -mt-40 relative z-10">
        <div className="flex flex-col md:flex-row gap-8">
          <img src={item.thumbnail} alt={item.title} onError={onImgError} className="w-44 sm:w-56 rounded-xl border border-white/10 shadow-2xl shrink-0 object-cover aspect-[2/3]" />
          <div className="flex-1 pt-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="badge-glass">{countryFlag(item.country)} {countryName(item.country)}</span>
              <span className="badge-glass">{item.language}</span>
              <span className="badge-glass !bg-[#D4AF37]/20 !border-[#D4AF37]/40 text-[#F5D97A]">{typeLabel(item.content_type)}</span>
              {item.subscription_required
                ? <span className="badge-glass !bg-[#E50914]/20 !border-[#E50914]/40"><Lock className="w-3 h-3" /> Premium</span>
                : <span className="badge-glass !bg-emerald-500/20 !border-emerald-400/40 text-emerald-300">Free</span>}
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl leading-none mb-3">{item.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/60 mb-4">
              {item.rating > 0 && <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" /> {item.rating} ({item.review_count})</span>}
              <span>{formatDuration(item.duration)}</span>
              <span className="px-2 py-0.5 rounded border border-white/20 text-xs">{item.age_rating}</span>
              <span>{new Date(item.release_date).getFullYear()}</span>
            </div>
            <p className="text-white/70 leading-relaxed max-w-3xl mb-6">{item.description}</p>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              {item.can_watch ? (
                <Link to={`/watch/${item.id}`} data-testid="detail-play-btn"
                  className="flex items-center gap-2 px-8 py-3 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-colors">
                  <Play className="w-5 h-5 fill-black" /> Play
                </Link>
              ) : item.pay_per_view_price > 0 ? (
                <button onClick={buyPPV} data-testid="detail-ppv-btn"
                  className="flex items-center gap-2 px-8 py-3 rounded-full bg-[#D4AF37] text-black font-semibold hover:bg-[#F59E0B] transition-colors">
                  <Lock className="w-5 h-5" /> Rent for R{item.pay_per_view_price}
                </button>
              ) : (
                <Link to="/subscriptions" data-testid="detail-subscribe-btn"
                  className="flex items-center gap-2 px-8 py-3 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors">
                  <Lock className="w-5 h-5" /> Subscribe to Watch
                </Link>
              )}
              <button onClick={toggleSave} data-testid="detail-save-btn"
                className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors">
                {item.saved ? <Check className="w-5 h-5 text-emerald-400" /> : <Plus className="w-5 h-5" />}
              </button>
              <button onClick={toggleLike} data-testid="detail-like-btn"
                className="flex items-center gap-2 px-4 h-12 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors">
                <Heart className={`w-5 h-5 ${item.liked ? "fill-[#E50914] text-[#E50914]" : ""}`} /> {item.like_count || 0}
              </button>
            </div>

            {user?.role === "ADMIN" && item.status !== "APPROVED" && (
              <div className="flex items-center gap-3 p-3 mb-6 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10" data-testid="admin-moderation-bar">
                <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold">Admin · {item.status}</span>
                <button onClick={() => moderate("APPROVED")} data-testid="detail-approve-btn" className="px-4 py-1.5 rounded-full bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400 transition-colors">Approve</button>
                <button onClick={() => moderate("REJECTED")} data-testid="detail-reject-btn" className="px-4 py-1.5 rounded-full bg-red-500/20 text-red-300 text-xs font-semibold hover:bg-red-500/30 transition-colors">Reject</button>
              </div>
            )}

            {item.creator && (
              <Link to={`/creator/${item.creator.id}`} className="inline-flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-[#E50914]/40 transition-colors">
                {item.creator.avatar ? <img src={item.creator.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="w-10 h-10 rounded-full bg-[#D4AF37]/30" />}
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Creator</p>
                  <p className="font-semibold flex items-center gap-1">{item.creator.display_name} {item.creator.verified && <BadgeCheck className="w-4 h-4 text-[#E50914]" />}</p>
                </div>
              </Link>
            )}
          </div>
        </div>

        {item.episodes?.length > 0 && (
          <div className="mt-10" data-testid="episodes-section">
            <h3 className="font-display font-bold text-lg mb-3">Episodes</h3>
            <div className="space-y-2 max-w-3xl">
              {item.episodes.map((ep) => (
                <Link key={ep.id} to={`/watch/${ep.id}`} data-testid={`episode-${ep.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:border-[#E50914]/40 transition-colors">
                  <img src={ep.thumbnail} onError={onImgError} alt="" className="w-24 h-14 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white/40">S{ep.season} · E{ep.episode_number}</p>
                    <p className="font-medium text-sm truncate">{ep.title}</p>
                  </div>
                  <Play className="w-5 h-5 fill-white" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {item.cast?.length > 0 && (
          <div className="mt-10">
            <h3 className="font-display font-bold text-lg mb-3">Cast</h3>
            <div className="flex flex-wrap gap-2">{item.cast.map((c) => <span key={c} className="badge-glass">{c}</span>)}</div>
          </div>
        )}

        <div className="mt-12 border-b border-white/10 flex gap-6">
          {["reviews", "comments"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`pb-3 text-sm font-medium capitalize transition-colors border-b-2 ${tab === t ? "border-[#E50914] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
              {t} ({t === "reviews" ? item.reviews?.length || 0 : item.comments?.length || 0})
            </button>
          ))}
        </div>

        <div className="py-6 max-w-3xl">
          {tab === "reviews" ? (
            <>
              {user && (
                <div className="glass rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setRating(n)} data-testid={`rate-${n}`}>
                        <Star className={`w-6 h-6 ${n <= rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-white/30"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={2} placeholder="Share your thoughts…"
                    data-testid="review-input" className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none resize-none mb-3" />
                  <button onClick={submitReview} data-testid="review-submit" className="px-5 py-2 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] text-sm font-semibold transition-colors">Post Review</button>
                </div>
              )}
              <div className="space-y-4">
                {item.reviews?.length ? item.reviews.map((r) => (
                  <div key={r.id} className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{r.user_name}</span>
                      <span className="flex items-center gap-0.5 text-xs text-[#F59E0B]"><Star className="w-3 h-3 fill-[#F59E0B]" /> {r.rating}</span>
                    </div>
                    <p className="text-sm text-white/70">{r.text}</p>
                  </div>
                )) : <p className="text-white/40 text-sm">No reviews yet. Be the first!</p>}
              </div>
            </>
          ) : (
            <>
              {user && (
                <div className="flex gap-2 mb-6">
                  <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" data-testid="comment-input"
                    className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none" />
                  <button onClick={submitComment} data-testid="comment-submit" className="w-11 h-11 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] flex items-center justify-center transition-colors">
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              )}
              <div className="space-y-3">
                {item.comments?.length ? item.comments.map((c) => (
                  <div key={c.id} className="flex gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#D4AF37]/30 flex items-center justify-center text-sm font-bold shrink-0">{c.user_name?.[0]}</span>
                    <div className="flex-1"><p className="text-sm font-semibold">{c.user_name}</p><p className="text-sm text-white/70">{c.text}</p></div>
                  </div>
                )) : <p className="text-white/40 text-sm">No comments yet.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Centered = ({ children }) => <div className="min-h-[60vh] flex items-center justify-center text-white/50">{children}</div>;
